/* AVELIQ 3D embed — scroll stability layer.

   FOUR fixes live here. All are containment for behaviour in
   assets/aveliq-hero-3d.js, which is 43KB and cannot be safely re-emitted
   through the API (whole-file replacement; a transcription slip there takes
   the home page down). Each one is annotated with the source-level fix that
   should replace it in a planned edit of that file.

   ------------------------------------------------------------------
   A. Fixed-layer containment
   ------------------------------------------------------------------
   #avq-bg, #avq-tint and #avq-webgl are all position:fixed / inset:0, so they
   cover the viewport at EVERY scroll position on the page. Nothing ties them
   to the 3D section — they are hidden only by GSAP tweens built inside the
   intro timeline, ~2.4s after load. An IntersectionObserver on the embed root
   is authoritative from first paint.

   rootMargin differs by pointer type: touch keeps the one-viewport grace that
   lets the product finish its exit ride-up (signed off on device); desktop
   uses zero, because IntersectionObserver has no notion of direction and the
   same margin released the clamp a viewport early when scrolling back UP.

   ------------------------------------------------------------------
   B. Resize-driven refresh storm (touch only by construction)
   ------------------------------------------------------------------
   The embed binds  addEventListener('resize', ... ScrollTrigger.refresh()).
   On iOS every address-bar move fires resize, re-measuring every pin spacer
   mid-gesture. Source fix: gate that handler on a width change.

   ------------------------------------------------------------------
   C. Back/forward cache restore (desktop)
   ------------------------------------------------------------------
   Back from a product page restores the DOM, scroll position and every inline
   style GSAP wrote, but `load` never fires and ScrollTrigger's cached offsets
   are never revalidated — stale pin maths (copy arriving after the model has
   finished turning) and a frozen half-opacity hero.

   ------------------------------------------------------------------
   D. Stale swapForm callbacks  <-- the re-entry bug
   ------------------------------------------------------------------
   swapForm() in the embed crossfades product groups by tweening group.scale
   between .001 and full, and hides the outgoing group in an onComplete:

       gsap.to(prevF.scale,{x:.001,...,duration:.4,
                            onComplete(){ prevF.visible=false; }});
       next.visible=true;
       gsap.fromTo(next.scale,{x:.001,...},{x:s,...,delay:.3});

   Neither call kills tweens already running on those same scale objects, and
   the onComplete captures `prevF` without re-checking whether it is still the
   outgoing form. Scroll out of the portfolio and back in — or reverse through
   two lines quickly — and line N's fade-OUT completes AFTER line N has been
   made current again, so its callback sets visible=false on the model that is
   currently scaling up. Result: the copy and the stage are correct, the pin
   progress is correct, and there is simply no product on screen. Reverse past
   it and the next swap restores things, which is why it looks intermittent
   and order-dependent rather than broken.

   The invariant that repairs it needs no knowledge of which line is active:
   a group being scaled up is by definition wanted on screen, so any group
   with a non-trivial scale must be visible. Enforced per frame below.

   Source fix: kill existing scale tweens at the top of swapForm, and guard
   the callback with  onComplete(){ if(curForm!==prevF) prevF.visible=false; } */
(function () {
  'use strict';

  var html = document.documentElement;
  var isTouch = window.matchMedia('(pointer: coarse)').matches;

  /* ---- B: refresh guard ------------------------------------------- */
  var tries = 0;
  (function installGuard() {
    var ST = window.ScrollTrigger;
    if (ST && !ST.__aqGuarded) {
      ST.__aqGuarded = true;

      var original = ST.refresh.bind(ST);
      ST.__aqForceRefresh = original;

      var lastW = window.innerWidth;
      var lastH = window.innerHeight;

      ST.refresh = function (safe) {
        var w = window.innerWidth;
        var h = window.innerHeight;
        var bigHeightJump = Math.abs(h - lastH) > lastH * 0.25;

        if (isTouch && w === lastW && !bigHeightJump) return;

        lastW = w;
        lastH = h;
        return original(safe);
      };

      try { ST.config({ ignoreMobileResize: true }); } catch (e) { /* older GSAP */ }
      return;
    }
    if (tries++ < 240) setTimeout(installGuard, 50);
  })();

  function settle() {
    var ST = window.ScrollTrigger;
    if (ST && ST.__aqForceRefresh) ST.__aqForceRefresh(true);
  }

  window.addEventListener('load', function () {
    setTimeout(settle, 1000);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { setTimeout(settle, 400); });
    }
  });

  window.addEventListener('orientationchange', function () {
    setTimeout(settle, 500);
  });

  /* ---- C: bfcache restore (desktop) -------------------------------- */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    if (isTouch) return;
    settle();
    setTimeout(settle, 250);   /* scroll position is restored asynchronously */
  });

  /* ---- D: product-group visibility watchdog (desktop) --------------
     Invariant: a group being scaled up is wanted on screen. Costs one loop
     over ~8 groups per frame, and only ever flips visible false -> true, so
     it cannot hide anything the embed intends to show. */
  var wTries = 0;
  (function watchForms() {
    if (isTouch) return;                       /* mobile embed is signed off */
    var api = window.AVQ;
    var product = api && api.three && api.three.product;

    if (!product) {
      if (wTries++ < 400) setTimeout(watchForms, 60);
      return;
    }

    /* form groups are the Group children of `product`; the contact shadow and
       the orbiting droplets are Meshes and are skipped */
    var groups = product.children.filter(function (c) { return c.type === 'Group'; });
    if (!groups.length) {
      if (wTries++ < 400) setTimeout(watchForms, 60);
      return;
    }

    (function frame() {
      requestAnimationFrame(frame);
      for (var i = 0; i < groups.length; i++) {
        var g = groups[i];
        if (!g.visible && g.scale.x > 0.02) g.visible = true;
      }
    })();
  })();

  /* ---- A: clamp the fixed layers to the embed ---------------------- */
  function bindVisibility() {
    var root = document.querySelector('.avq-embed-root');
    if (!root) return;

    if (!('IntersectionObserver' in window)) {
      html.classList.add('avq-embed-ready');
      return;
    }

    html.classList.add('avq-embed-out');

    var margin = isTouch ? '100% 0px 0px 0px' : '0px';

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          html.classList.toggle('avq-embed-out', !entry.isIntersecting);
          if (entry.isIntersecting) html.classList.add('avq-embed-ready');
        });
      },
      { rootMargin: margin, threshold: 0 }
    ).observe(root);

    var box = root.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) {
      html.classList.add('avq-embed-ready');
      html.classList.remove('avq-embed-out');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindVisibility);
  } else {
    bindVisibility();
  }
})();
